import { Engine, World, Bodies, Body, Composite, Events } from 'matter-js';
import {
  CONTAINER_LEFT, CONTAINER_RIGHT, CONTAINER_BOTTOM, CONTAINER_W,
  H, DEATH_LINE_Y, GRACE_MS, MAX_TIER, FruitData,
} from './types';
import { fruitRadius } from './fruits';

// Physics wall thickness (the visible stroke is only 6px — see renderer). Thick
// solid walls prevent small fast fruits from tunnelling through between steps.
const PHYS_WALL = 60;
// Hard cap on per-step displacement (px). Normal fall peaks ~25px/step; this only
// catches pathological speeds that could skip a wall.
const MAX_SPEED = 40;

export interface MergeResult {
  newTier: number;      // tier of the spawned fruit, 0 if none (double watermelon)
  x: number;
  y: number;
  doubleWatermelon: boolean;
}

let engine: Engine;

// Cache of fruit bodies, invalidated whenever the world's fruit composition
// changes (spawnFruit, resolveMerges, reset). Bodies don't move in/out of the
// world between physics substeps within a frame, so the cache is safe to reuse
// across the step() loop, findOverlapMerges(), checkGameOver() and renderFrame().
let cachedFruitBodies: Body[] | null = null;

// Raw pairs captured during a physics step, resolved afterwards by resolveMerges().
let pending: { a: Body; b: Body }[] = [];
const consumed = new Set<number>(); // Matter body ids already claimed by a merge this step

export function initPhysics(): void {
  engine = Engine.create();
  engine.gravity.y = 1.0;

  const wallOpts = { isStatic: true, friction: 0.3, restitution: 0.2 };
  // Inner faces stay exactly on the container boundary; mass extends outward.
  const left = Bodies.rectangle(
    CONTAINER_LEFT - PHYS_WALL / 2, H / 2, PHYS_WALL, H * 2, wallOpts);
  const right = Bodies.rectangle(
    CONTAINER_RIGHT + PHYS_WALL / 2, H / 2, PHYS_WALL, H * 2, wallOpts);
  const bottom = Bodies.rectangle(
    (CONTAINER_LEFT + CONTAINER_RIGHT) / 2, CONTAINER_BOTTOM + PHYS_WALL / 2,
    CONTAINER_W + PHYS_WALL * 2, PHYS_WALL, wallOpts);
  World.add(engine.world, [left, right, bottom]);

  Events.on(engine, 'collisionStart', (evt) => {
    for (const pair of evt.pairs) {
      const a = pair.bodyA, b = pair.bodyB;
      const fa = a.plugin?.fruit as FruitData | undefined;
      const fb = b.plugin?.fruit as FruitData | undefined;
      if (!fa || !fb || fa.tier !== fb.tier) continue;
      if (consumed.has(a.id) || consumed.has(b.id)) continue;
      consumed.add(a.id);
      consumed.add(b.id);
      pending.push({ a, b });
    }
  });
}

// Small fruits get a gentle density boost so they shove heavier ones a bit when
// they land. Ramps down across tiers 1–4; tier 5+ stays at the base density.
function tierDensity(tier: number): number {
  const mul = tier <= 4 ? 1 + (5 - tier) * 0.18 : 1;  // t1≈1.72 … t4≈1.18, else 1
  return 0.001 * mul;
}

export function spawnFruit(
  tier: number, x: number, y: number, nowMs: number, scaleFrom: number,
): Body {
  const r = fruitRadius(tier);
  const body = Bodies.circle(x, y, r, {
    density: tierDensity(tier),
    friction: 0.3,
    restitution: 0.2,
    frictionAir: 0.01,
  });
  const data: FruitData = { tier, spawnMs: nowMs, scaleFrom };
  body.label = 'fruit';
  body.plugin = { fruit: data };
  World.add(engine.world, body);
  cachedFruitBodies = null;
  return body;
}

export function step(dtMs: number): void {
  Engine.update(engine, dtMs);
  // Clamp pathological speeds so nothing can skip a wall in one step.
  for (const b of fruitBodies()) {
    const v = b.velocity;
    const sp = Math.hypot(v.x, v.y);
    if (sp > MAX_SPEED) {
      const k = MAX_SPEED / sp;
      Body.setVelocity(b, { x: v.x * k, y: v.y * k });
    }
  }
}

// Safety-net sweep: merge any pair of same-tier fruits that are touching but were
// never caught by a collisionStart event (e.g. a merged fruit spawned already
// overlapping a neighbour, or two settled fruits resting in contact). Queues the
// pairs into `pending`; resolveMerges() does the actual removal/spawn.
export function findOverlapMerges(): void {
  const bodies = fruitBodies();
  for (let i = 0; i < bodies.length; i++) {
    const a = bodies[i];
    const fa = a.plugin.fruit as FruitData;
    if (consumed.has(a.id)) continue;
    for (let j = i + 1; j < bodies.length; j++) {
      const b = bodies[j];
      const fb = b.plugin.fruit as FruitData;
      if (fa.tier !== fb.tier || consumed.has(b.id)) continue;
      const dist = Math.hypot(a.position.x - b.position.x, a.position.y - b.position.y);
      if (dist <= fruitRadius(fa.tier) + fruitRadius(fb.tier) + 1.5) {
        consumed.add(a.id);
        consumed.add(b.id);
        pending.push({ a, b });
        break; // a is now consumed; move to the next i
      }
    }
  }
}

// Resolve all merges queued during the last step(s). Removes consumed bodies,
// spawns the next-tier fruit, and returns one MergeResult per merge.
export function resolveMerges(nowMs: number): MergeResult[] {
  const results: MergeResult[] = [];
  for (const { a, b } of pending) {
    const fa = a.plugin.fruit as FruitData;
    const tier = fa.tier;
    const x = (a.position.x + b.position.x) / 2;
    const y = (a.position.y + b.position.y) / 2;
    World.remove(engine.world, a);
    World.remove(engine.world, b);
    cachedFruitBodies = null;

    if (tier >= MAX_TIER) {
      results.push({ newTier: 0, x, y, doubleWatermelon: true });
    } else {
      spawnFruit(tier + 1, x, y, nowMs, 0.6);
      results.push({ newTier: tier + 1, x, y, doubleWatermelon: false });
    }
  }
  pending = [];
  consumed.clear();
  return results;
}

export function fruitBodies(): Body[] {
  if (!cachedFruitBodies) {
    cachedFruitBodies = Composite.allBodies(engine.world).filter((b) => b.label === 'fruit');
  }
  return cachedFruitBodies;
}

// Called when resuming from pause: shifts every fruit's spawn timestamp forward by
// the paused duration, so the grace period (measured against wall-clock spawnMs)
// doesn't get eaten by time the physics wasn't actually running.
export function shiftSpawnTimes(deltaMs: number): void {
  for (const b of fruitBodies()) {
    (b.plugin.fruit as FruitData).spawnMs += deltaMs;
  }
}

export function checkGameOver(nowMs: number): boolean {
  for (const b of fruitBodies()) {
    const f = b.plugin.fruit as FruitData;
    if (nowMs - f.spawnMs > GRACE_MS && b.position.y < DEATH_LINE_Y) {
      return true;
    }
  }
  return false;
}

export function reset(): void {
  for (const b of fruitBodies()) World.remove(engine.world, b);
  cachedFruitBodies = null;
  pending = [];
  consumed.clear();
}
